import { useEffect, useRef, useState } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import {
  T, Icon, stitchCard, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn,
  stitchSectionLabel, stitchTooltipSlotProps,
} from '../ProjectOverview/parts/stitch';
import FaseTaakRij from './FaseTaakRij';
import { SnelRegel, TaakMetSubtaken } from './FaseSubtaken';
import { bouwBoom, kentSubtaken, losAfgerond, ouderKaart, typeVoorGroep } from './subtaken';
import { useProjectData } from '../ProjectOverview/ProjectDataContext';
import { isTaakAfgerond } from '../ProjectOverview/parts/projectMeta';
import ConceptTrainer from './ConceptTrainer';
import DossierBlok from './DossierBlok';
import FaseTemplates from './FaseTemplates';
import SourceSearchBox from '../Offertes/SourceSearchBox';
import GenResultDialog from './GenResultDialog';
import {
  TEKST, faseLabel, faseStatusZin, splitsDetails, stilleKopSx, stilleLinkSx, stilleZoneSx, volgendeStap,
} from './rustigeKop';
import { FLOW_PHASES, PANEL_LABELS, STATUS_STYLES, buildToolUrl } from '../../config/projectFlow';

/**
 * Het werkvlak van de geselecteerde fase: status mét bewijs, de taken van deze
 * fase, het briefingblok (de iPad-test — lege velden staan er als "niet
 * ingevuld"), de documentgenerator, de tools en de detailpanelen mét aantallen.
 */
function StagePanel({ phase, project, onOpenPaneel, onOpenDialog }) {
  const navigate = useNavigate();
  const {
    tasks, deliverables, notes, transcripts, quitclaims, draaidagen, callsheets,
    frameioVideos, qaReviews, quoteLines, setSelectedTask, handleToggleComplete,
    loadData, voegTaakToe, handleTaskDelete,
  } = useProjectData();
  const meta = FLOW_PHASES[phase?.phase_key] || { tools: [], panels: [] };
  const stijl = STATUS_STYLES[phase?.status] || STATUS_STYLES.todo;
  const projectRecId = project?.airtable_id || project?.id;
  const [mailKoppelOpen, setMailKoppelOpen] = useState(false);
  // Gereedschap standaard ingeklapt; de keuze onthouden we (alle projecten).
  const [gereedschapOpen, setGereedschapOpen] = useState(() => {
    try { return localStorage.getItem('flowGereedschapOpen') === '1'; } catch { return false; }
  });
  const wisselGereedschap = () => {
    setGereedschapOpen((v) => {
      try { localStorage.setItem('flowGereedschapOpen', v ? '0' : '1'); } catch { /* privémodus */ }
      return !v;
    });
  };
  const aantalGereedschap = (meta.dialogActions || []).length + 1 + (meta.tools || []).length;
  const [toonAlleDetails, setToonAlleDetails] = useState(false);

  // Mail/notulen/meeting koppelen — zelfde zoeker als de offerte-generator
  // (SourceSearchBox), maar hier landt een treffer als projectnotitie.
  const koppelBronAlsNotitie = async (blok) => {
    const tekst = String(blok || '').trim();
    if (!tekst || !projectRecId) return;
    const kop = (tekst.split('\n')[0] || '').replace(/^[—\s]+|[—\s]+$/g, '');
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectRecId,
          title: (kop || 'Gekoppelde e-mail').slice(0, 120),
          notes: tekst,
          source: 'Koppeling (mail/notulen)',
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      // Zonder herladen bleef de notitiepaneel op de oude lijst staan: je kreeg een
      // melding dat het gelukt was en zag er niets van terug (Joris, 11 sep 2026).
      await loadData?.();
      toast.success('Gekoppeld als projectnotitie — staat bij Notities');
    } catch (e) { toast.error(`Koppelen mislukt: ${e.message}`); }
  };

  // Aantallen op de detail-chips: "Draaidagen (2)" leest sneller dan doorklikken.
  const PANEL_COUNTS = {
    taken: tasks.length, deliverables: deliverables.length, notes: notes.length,
    meetings: transcripts.length, quitclaims: quitclaims.length,
    draaidagen: draaidagen.length, callsheets: callsheets.length,
    frameio: frameioVideos.length, qa: qaReviews.length,
    offerteregels: quoteLines?.count ?? null, activiteit: null, correspondentie: null,
  };
  const details = splitsDetails(meta.panels || [], PANEL_COUNTS);
  // Statusregel bovenaan: de eerstvolgende open taak van het hele project.
  const volgende = volgendeStap(tasks, undefined, deliverables);

  // De taken van deze fase — zelfde type-bril als de status-afleiding.
  // Gearchiveerde taken (o.a. vervallen draaidag-OPTIES) horen hier niet meer.
  const faseTaken = (meta.taskTypes || []).length
    ? tasks.filter((t) => (meta.taskTypes || []).includes(t.fields?.Type)
        && t.fields?.PrioNr !== 'Gearchiveerd')
    : [];
  const [toonAfgeronde, setToonAfgeronde] = useState(false);
  // Inklappen per deliverable (Joris, 10 sep 2026: een fase met 11 taken duwt de
  // rest van het paneel uit beeld). De keuze staat per project én fase in
  // localStorage, zodat je bij terugkomen niet opnieuw zit te klappen.
  const klapSleutel = `projectflow-taken-dicht:${projectRecId || 'x'}:${phase?.phase_key || 'x'}`;
  const [dichteGroepen, setDichteGroepen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(klapSleutel) || '[]')); }
    catch { return new Set(); }
  });
  const bewaarDicht = (volgende) => {
    setDichteGroepen(volgende);
    try { localStorage.setItem(klapSleutel, JSON.stringify([...volgende])); } catch { /* privémodus */ }
  };
  // Andere fase = andere sleutel; zonder dit blijft de stand van de vorige fase hangen.
  useEffect(() => {
    try { setDichteGroepen(new Set(JSON.parse(localStorage.getItem(klapSleutel) || '[]'))); }
    catch { setDichteGroepen(new Set()); }
  }, [klapSleutel]);
  const wisselGroep = (key) => {
    const volgende = new Set(dichteGroepen);
    if (volgende.has(key)) volgende.delete(key); else volgende.add(key);
    bewaarDicht(volgende);
  };
  // Bulk: alle open fase-taken in één keer afronden (oude taken opruimen).
  const [allesAfvinken, setAllesAfvinken] = useState(false);
  const vinkAllesAf = async () => {
    if (!(await bevestig({ titel: `Alle ${openTaken.length} open taken afvinken?`, tekst: 'Alle open taken in deze fase worden als afgerond gemarkeerd.', knop: 'Afvinken' }))) return;
    setAllesAfvinken(true);
    let gelukt = 0;
    try {
      for (const t of openTaken) {
        // Bulk: geen undo-toast per taak.
        try { await handleToggleComplete(t, true, { metOngedaan: false }); gelukt += 1; }
        catch { /* doortellen; melding volgt hieronder */ }
      }
      if (gelukt === openTaken.length) toast.success(`${gelukt} taken afgevinkt`);
      else toast.error(`${gelukt}/${openTaken.length} afgevinkt — probeer de rest opnieuw`);
    } finally { setAllesAfvinken(false); }
  };
  // Bulk: alle open taken van één deliverable in deze fase weg (wiki 26 sep: "ooit
  // ingepland maar kan allemaal weg"). Echt verwijderen is niet terug te draaien,
  // dus eerst bevestigen; afgeronde taken blijven staan.
  const [groepWeg, setGroepWeg] = useState(null);
  // Alleen écht open taken: ook PrioNr Afgerond/Opgestuurd zonder vinkje blijft staan.
  const openInGroep = (groep) => groep.taken.filter((t) => !isTaakAfgerond(t));
  const verwijderGroep = async (groep) => {
    const weg = openInGroep(groep);
    const n = weg.length;
    if (!n || groepWeg) return;
    const ok = await bevestig({
      titel: `${n} ${n === 1 ? 'taak' : 'taken'} van "${groep.naam}" verwijderen?`,
      tekst: `Alle open taken onder deze deliverable in ${phase?.label || 'deze fase'} verdwijnen uit de planning. Afgeronde taken blijven staan. Dit kan niet ongedaan worden gemaakt.`,
      knop: n === 1 ? 'Verwijderen' : `${n} taken verwijderen`,
      gevaarlijk: true,
    });
    if (!ok) return;
    setGroepWeg(groep.key);
    let gelukt = 0;
    try {
      for (const t of weg) {
        // alleenOpen: de engine checkt vlak voor het verwijderen of de taak nog open is.
        try { await handleTaskDelete(t.id, { stil: true, alleenOpen: true }); gelukt += 1; } // eslint-disable-line no-await-in-loop
        catch { /* doortellen; melding volgt hieronder */ }
      }
    } finally { setGroepWeg(null); }
    if (gelukt === n) toast.success(`${gelukt} ${gelukt === 1 ? 'taak' : 'taken'} verwijderd`);
    else toast.error(`${gelukt}/${n} verwijderd — de rest is intussen afgerond of kon niet weg`);
  };
  // Chronologisch: je moet de timing voelen. Zonder datum → onderaan.
  const taakDatum = (t) => t.fields?.StartDateUitvoerder || t.fields?.EndDateUitvoerder || t.fields?.Deadline || '9999';
  const opDatum = (arr) => [...arr].sort((a, b) => String(taakDatum(a)).localeCompare(String(taakDatum(b))));
  const openTaken = opDatum(faseTaken.filter((t) => t.fields?.Afgerond !== true));
  const klareTaken = opDatum(faseTaken.filter((t) => t.fields?.Afgerond === true));
  // Subtaken (sep 2026): ingesprongen onder hun ouder. Levert de engine de velden niet
  // (oude engine), dan is er geen ouder bekend en blijft alles plat.
  const subKaart = ouderKaart(faseTaken);
  const kanSub = kentSubtaken(tasks);
  const losKlaar = losAfgerond(klareTaken, openTaken, subKaart);
  const subSleutel = `projectflow-subtaken-dicht:${projectRecId || 'x'}`;
  const [dichteOuders, setDichteOuders] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(subSleutel) || '[]')); } catch { return new Set(); }
  });
  const wisselOuder = (ouderId) => {
    const volgende = new Set(dichteOuders);
    if (volgende.has(ouderId)) volgende.delete(ouderId); else volgende.add(ouderId);
    setDichteOuders(volgende);
    try { localStorage.setItem(subSleutel, JSON.stringify([...volgende])); } catch { /* privémodus */ }
  };
  // Groeperen per deliverable (9487-02, 9487-03, …) — daar zitten de
  // afhankelijkheden: V1 → V2 → DEF binnen dezelfde deliverable.
  // Let op de veldnaam: de spiegel noemt hem 'Naam van Deliverable'. Met de
  // oude gok stond er boven elke groep letterlijk "Deliverable" (Joris, 11 sep).
  const delNaam = new Map((deliverables || []).map((d) => [
    d.id,
    d.fields?.['Naam van Deliverable'] || d.fields?.Deliverable || d.fields?.Naam
      || d.name || 'Deliverable',
  ]));
  const taakGroepen = (() => {
    const per = new Map();
    openTaken.forEach((t) => {
      const key = (t.fields?.Deliverable || [])[0] || '_overig';
      if (!per.has(key)) per.set(key, []);
      per.get(key).push(t);
    });
    return [...per.entries()].map(([key, taken]) => ({
      key, taken, naam: key === '_overig' ? 'Overige taken' : (delNaam.get(key) || 'Deliverable'),
    }));
  })();

  const allesDicht = taakGroepen.length > 0 && taakGroepen.every((g) => dichteGroepen.has(g.key));

  // Documentgenerator (debrief → markdown-dialog, concept → PowerPoint-download)
  const [genBusy, setGenBusy] = useState(false);
  const [trainerOpen, setTrainerOpen] = useState(false);
  const [genResult, setGenResult] = useState(null); // { titel, markdown }
  const [checkBusy, setCheckBusy] = useState(false);
  // { id, naam, notities } — onderwerp hernoemen en notities bij een klantbriefing.
  const [briefingEdit, setBriefingEdit] = useState(null);
  const briefingFileRef = useRef(null);

  // Klantbriefing: eerst BEWAREN bij het project, dan analyseren. Andersom kost
  // een mislukte AI-call je het document; nu blijft de briefing staan en kun je
  // de analyse later overdoen.
  const [plakOpen, setPlakOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [plakTekst, setPlakTekst] = useState('');
  const [briefingDocs, setBriefingDocs] = useState([]);

  const laadBriefingDocs = async () => {
    if (!projectRecId) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings`);
      const d = r.ok ? await r.json() : null;
      setBriefingDocs(d?.briefings || []);
    } catch { /* lijst is aanvullend */ }
  };
  // Ook buiten de opstartfase laden: het Airtable-briefingformulier is vaak leeg
  // en dan is "geen briefing gekoppeld" verwarrend als er wél een klantbriefing
  // bij het project staat.
  useEffect(() => {
    laadBriefingDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectRecId]);

  const bewaarBriefingTekst = async (text, label, bron) => {
    if (!String(text || '').trim() || checkBusy) return;
    setCheckBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tekst: text, bestandsnaam: label, bron }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      setPlakOpen(false);
      setPlakTekst('');
      await laadBriefingDocs();
      if (d.analyse_fout) {
        toast(`Briefing bewaard — de analyse lukte niet (${d.analyse_fout}). Je kunt 'm later opnieuw laten lezen.`, { icon: '⚠️' });
      } else {
        toast.success('Briefing bewaard bij dit project en geanalyseerd');
        setGenResult({ titel: `Briefing-check — ${label}`, markdown: d.briefing?.analyse_markdown || '' });
      }
    } catch (e) { toast.error(`Briefing opslaan mislukt: ${e.message}`); }
    finally { setCheckBusy(false); }
  };

  const uploadKlantBriefing = async (file) => {
    if (!file || checkBusy) return;
    setCheckBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const pr = await authFetch(`${BACKEND_URL}/Api/parse-attachment`, { method: 'POST', body: fd });
      if (!pr.ok) throw new Error((await pr.json().catch(() => ({}))).error || 'Kon het document niet lezen');
      const { text } = await pr.json();
      setCheckBusy(false);
      if (!String(text || '').trim()) throw new Error('Geen tekst gevonden in het document');
      await bewaarBriefingTekst(text, file.name, 'upload');
    } catch (e) { setCheckBusy(false); toast.error(`Briefing opslaan mislukt: ${e.message}`); }
  };

  const heranalyseerBriefingDoc = async (id) => {
    if (checkBusy) return;
    setCheckBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings/${id}/heranalyseer`, { method: 'POST' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      await laadBriefingDocs();
      setGenResult({ titel: `Briefing-check — ${d.briefing?.bestandsnaam || 'klantbriefing'}`, markdown: d.briefing?.analyse_markdown || '' });
    } catch (e) { toast.error(`Opnieuw lezen mislukt: ${e.message}`); }
    finally { setCheckBusy(false); }
  };

  const bewaarBriefingWijziging = async (id) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bestandsnaam: briefingEdit?.naam || '', notities: briefingEdit?.notities || '' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setBriefingEdit(null);
      await laadBriefingDocs();
      toast.success('Briefing bijgewerkt');
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); }
  };

  const verwijderBriefingDoc = async (id) => {
    if (!(await bevestig({ titel: 'Briefing verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await laadBriefingDocs();
      toast.success('Briefing verwijderd');
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
  };

  const runGenerator = async () => {
    if (!meta.generator || genBusy) return;
    setGenBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/document`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: meta.generator.kind }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${r.status}`);
      }
      if (meta.generator.output === 'pptx') {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `concept-${project?.fields?.Projectnummer || 'project'}.pptx`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Conceptpresentatie gedownload');
      } else {
        const d = await r.json();
        setGenResult({ titel: 'Debrief — gegenereerd door Fable 5', markdown: d.markdown || '' });
      }
    } catch (e) { toast.error(`Genereren mislukt: ${e.message}`); }
    finally { setGenBusy(false); }
  };

  if (!phase) return null;

  return (
    <Box sx={{ ...stitchCard, p: { xs: 2, md: 3 }, '&:hover': { boxShadow: T.shadow, transform: 'none' } }}
      onDragOver={phase.phase_key === 'opstartfase' ? (e) => { e.preventDefault(); setDragActive(true); } : undefined}>
      {/* Eén statusregel: wat is de eerstvolgende stap van dit project? */}
      {volgende && (
        <Box component="button" type="button" onClick={() => setSelectedTask(volgende.taak)}
          aria-label={volgende.tekst}
          sx={{ ...stilleLinkSx, color: T.outline, mb: 2, maxWidth: '100%', textAlign: 'left',
            display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Volgende:{' '}
          <Box component="span" sx={{ color: T.text, fontWeight: 600 }}>{volgende.naam}</Box>
          {volgende.deadline ? ' ' : ', '}
          <Box component="span" sx={volgende.teLaat ? { color: T.error, fontWeight: 600 } : undefined}>{volgende.datum}</Box>
          {' · '}{volgende.eigenaar}
        </Box>
      )}

      {/* Kop: fase + één stille statuszin, één primaire actie (Joris, 25 sep:
          rustiger — secundaire acties als tekstlink). */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          {/* Rust: de fase-uitleg staat achter mouse-over */}
          <Tooltip title={meta.beschrijving || ''} slotProps={stitchTooltipSlotProps}>
            <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1rem', color: T.text, cursor: 'default' }}>
              {phase.label}
            </Typography>
          </Tooltip>
          {/* Status + eerste bewijs als één zin; de rest + bron in de tooltip */}
          <Tooltip slotProps={stitchTooltipSlotProps}
            title={[...(phase.evidence || []), ...(phase.deels ? ['een eerdere fase is nog niet af'] : []),
              `bron: ${phase.status_source}`].join(' · ')}>
            <Typography sx={{ mt: 0.25, fontFamily: T.fontBody, fontSize: TEKST, color: T.outline, cursor: 'default' }}>
              {faseStatusZin(faseLabel(phase, stijl.label), phase.evidence)}
              {(phase.evidence || []).length > 1 && ` (+${phase.evidence.length - 1})`}
            </Typography>
          </Tooltip>
          {/* Offerte belooft script/concept/kick-off maar er is geen taak, document of
              meeting voor (wiki 26 sep) — alleen een waarschuwing, blokkeert niets. */}
          {phase.waarschuwing && (
            <Tooltip slotProps={stitchTooltipSlotProps}
              title="Staat in de offerteregels, maar er is nog geen taak, document of gekoppelde meeting voor. Alleen een waarschuwing — plannen mag gewoon.">
              <Typography data-testid="fase-waarschuwing"
                sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.75, fontFamily: T.fontBody, fontSize: TEKST, color: T.outline, cursor: 'default' }}>
                <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: T.amber, flexShrink: 0 }} />
                {phase.waarschuwing}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {/* Postproductie: de automatische planner (Taken genereren + planningspuzzel)
            direct vanaf de fase starten (wiki 26 sep: "knop beschikbaar maken"). */}
        {phase.phase_key === 'postproductie' && onOpenDialog && (
          <Tooltip slotProps={stitchTooltipSlotProps}
            title="Genereert de montage-/animatietaken per deliverable en plant ze tot de deadline in (dezelfde planner als bij Deliverables → Taken genereren)">
            <Box component="span" role="button" tabIndex={0} aria-label="Postproductie inplannen" onClick={() => onOpenDialog('postprodPlanner')}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpenDialog('postprodPlanner'); }}
              sx={{ ...stitchGradientBtn, fontSize: TEKST, alignSelf: 'center' }}>
              <Icon name="event_upcoming" size={17} /> Postproductie inplannen
            </Box>
          </Tooltip>
        )}
        {/* Documentgenerator van deze fase (+ trainen bij het concept) */}
        {meta.generator && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
            {phase.phase_key === 'opstartfase' && (
              <>
                <input ref={briefingFileRef} type="file" hidden accept=".pdf,.docx,.pptx,.txt,.md"
                  onChange={(e) => { uploadKlantBriefing(e.target.files?.[0]); e.target.value = ''; }} />
                <Tooltip title="Bewaart het klantdocument bij dit project en checkt wat er ontbreekt" slotProps={stitchTooltipSlotProps}>
                  <Box component="button" type="button" disabled={checkBusy}
                    onClick={() => briefingFileRef.current?.click()} sx={stilleLinkSx}>
                    {checkBusy && <CircularProgress size={13} />}
                    {checkBusy ? 'Bewaren…' : 'Klantbriefing uploaden'}
                  </Box>
                </Tooltip>
                <Tooltip title="Mail of tekst plakken i.p.v. bestand" slotProps={stitchTooltipSlotProps}>
                  <Box component="button" type="button" disabled={checkBusy}
                    onClick={() => setPlakOpen(true)} sx={stilleLinkSx}>
                    plakken
                  </Box>
                </Tooltip>
              </>
            )}
            {meta.generator.kind === 'concept' && (
              <Tooltip title="Voed de conceptmachine met voorbeelden en richtlijnen" slotProps={stitchTooltipSlotProps}>
                <Box component="button" type="button" onClick={() => setTrainerOpen(true)} sx={stilleLinkSx}>
                  Trainen
                </Box>
              </Tooltip>
            )}
            <Tooltip title={meta.generator.uitleg} slotProps={stitchTooltipSlotProps}>
              <Box component="span" onClick={runGenerator}
                sx={{ ...stitchGradientBtn, fontSize: TEKST, opacity: genBusy ? 0.6 : 1 }}>
                {genBusy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Icon name="auto_awesome" size={17} />}
                {genBusy ? 'Fable 5 schrijft…' : meta.generator.label}
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Taken van deze fase */}
      {(meta.taskTypes || []).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography sx={stilleKopSx}>
              Taken in deze fase
              <Box component="span" sx={{ color: T.outline, fontWeight: 400 }}> · {klareTaken.length}/{faseTaken.length} af</Box>
            </Typography>
            <Box sx={{ flex: 1 }} />
            {taakGroepen.length > 0 && (
              <Box component="button" type="button" sx={stilleLinkSx}
                onClick={() => bewaarDicht(allesDicht ? new Set() : new Set(taakGroepen.map((g) => g.key)))}>
                {allesDicht ? 'uitklappen' : 'inklappen'}
              </Box>
            )}
            {openTaken.length > 1 && (
              <Tooltip title="Alle open taken in deze fase in één keer afronden — handig om oude taken op te ruimen" slotProps={stitchTooltipSlotProps}>
                <Box component="button" type="button" onClick={vinkAllesAf} disabled={allesAfvinken} sx={stilleLinkSx}>
                  {allesAfvinken && <CircularProgress size={11} />}
                  alles afvinken
                </Box>
              </Tooltip>
            )}
          </Box>
          {faseTaken.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Tooltip title="Genereer projecttaken via het Taken-paneel of 'Projecttaken genereren'" slotProps={stitchTooltipSlotProps}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: TEKST, color: T.outline, cursor: 'default' }}>
                  Nog geen taken
                </Typography>
              </Tooltip>
              <Box sx={{ ml: -1.5, display: 'flex', flexDirection: 'column' }}>
                <SnelRegel label="+ taak in deze fase" ariaLabel={`Nieuwe taak in ${phase.label}`}
                  onVoegToe={(naam) => voegTaakToe({ naam, type: typeVoorGroep([], meta.taskTypes) })} />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {taakGroepen.map((groep) => {
                const dicht = dichteGroepen.has(groep.key);
                return (
                  <Box key={groep.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mt: 0.5,
                      '& .groep-weg': { opacity: groepWeg === groep.key ? 1 : 0, transition: 'opacity 0.15s ease' },
                      '&:hover .groep-weg, & .groep-weg:focus-visible': { opacity: 1 } }}>
                      <Box component="button" onClick={() => wisselGroep(groep.key)}
                        aria-expanded={!dicht}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0, flex: 1, minWidth: 0,
                          border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                        <Icon name={dicht ? 'chevron_right' : 'expand_more'} size={15} />
                        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, fontWeight: 700 }}>
                          {groep.naam}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, fontWeight: 700 }}>
                          ({groep.taken.length})
                        </Typography>
                      </Box>
                      {openInGroep(groep).length > 0 && (
                      <Box component="button" type="button" className="groep-weg" disabled={!!groepWeg}
                        onClick={() => verwijderGroep(groep)}
                        aria-label={`Alle ${openInGroep(groep).length} open taken van ${groep.naam} verwijderen`}
                        sx={{ ...stilleLinkSx, fontSize: '0.72rem', '&:hover': { color: T.error } }}>
                        {groepWeg === groep.key ? <CircularProgress size={11} /> : <Icon name="delete" size={14} />}
                        {groepWeg === groep.key ? 'verwijderen…' : 'alle taken verwijderen'}
                      </Box>
                      )}
                    </Box>
                    {!dicht && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {bouwBoom(groep.taken, faseTaken, subKaart).map((knoop) => (
                          <TaakMetSubtaken
                            key={knoop.taak.id} knoop={knoop} dicht={dichteOuders.has(knoop.taak.id)}
                            onWissel={() => wisselOuder(knoop.taak.id)} onOpen={setSelectedTask}
                            onToggleComplete={handleToggleComplete} kanSub={kanSub}
                            onSubtaak={(naam) => voegTaakToe({ naam, parentTaskId: knoop.taak.id })}
                          />
                        ))}
                        {groep.key !== '_overig' && (
                          <SnelRegel ariaLabel={`Nieuwe taak voor ${groep.naam}`}
                            onVoegToe={(naam) => voegTaakToe({ naam, deliverableId: groep.key, type: typeVoorGroep(groep.taken, meta.taskTypes) })} />
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
              <SnelRegel label="+ taak in deze fase" ariaLabel={`Nieuwe taak in ${phase.label}`}
                onVoegToe={(naam) => voegTaakToe({ naam, type: typeVoorGroep([], meta.taskTypes) })} />
              {losKlaar.length > 0 && (
                <Box component="button" type="button" onClick={() => setToonAfgeronde((v) => !v)}
                  sx={{ ...stilleLinkSx, alignSelf: 'flex-start' }}>
                  {toonAfgeronde ? 'Verberg afgeronde taken' : `Toon ${losKlaar.length} afgeronde taken`}
                </Box>
              )}
              {toonAfgeronde && losKlaar.map((t) => (
                <FaseTaakRij key={t.id} task={t} onClick={() => setSelectedTask(t)} onToggleComplete={handleToggleComplete} />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Klantbriefing er direct in slepen — het grote vak alleen zolang er nog
          geen briefing is (of tijdens het slepen zelf); daarna volstaat de knop. */}
      {phase.phase_key === 'opstartfase' && (briefingDocs.length === 0 || dragActive) && (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragActive(false);
            const file = e.dataTransfer?.files?.[0];
            if (file) uploadKlantBriefing(file);
          }}
          onClick={() => !checkBusy && briefingFileRef.current?.click()}
          sx={{
            mb: 2.5, p: 2, borderRadius: '0.75rem', cursor: 'pointer', textAlign: 'center',
            border: `2px dashed ${dragActive ? T.tertiary : T.outlineVariant}`,
            bgcolor: dragActive ? T.tertiaryContainer + '40' : 'transparent',
            transition: 'all 0.15s ease', '&:hover': { borderColor: T.tertiary },
          }}>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: dragActive ? T.tertiary : T.outline,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
            {checkBusy ? <CircularProgress size={14} /> : <Icon name="place_item" size={17} />}
            {checkBusy ? 'Bewaren…' : 'Sleep de klantbriefing hierheen of klik'}
          </Typography>
        </Box>
      )}

      {/* Wat de klant zelf aanleverde — bewaard bij het project, los van ons
          Airtable-briefingformulier. */}
      {briefingDocs.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ ...stilleKopSx, mb: 1 }}>
            Klantbriefing bij dit project ({briefingDocs.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {briefingDocs.map((doc) => {
              // Sleutels met een liggend streepje zijn intern (bv. _notities) en
              // horen niet tussen de kernveld-chips.
              const velden = Object.entries(doc.velden || {}).filter(([k, v]) => v && !k.startsWith('_'));
              const notities = doc.velden?._notities || '';
              const inBewerking = briefingEdit?.id === doc.id;
              return (
                <Box key={doc.id} sx={{ border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.6rem', p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Icon name={doc.bron === 'plak' ? 'content_paste' : 'description'} size={17} sx={{ color: T.tertiary }} />
                    <Tooltip title="Klik om het onderwerp te wijzigen" slotProps={stitchTooltipSlotProps}>
                      <Typography
                        onClick={() => setBriefingEdit({ id: doc.id, naam: doc.bestandsnaam || '', notities })}
                        sx={{
                          fontFamily: T.font, fontWeight: 800, fontSize: '0.85rem', color: T.text,
                          cursor: 'pointer', borderBottom: `1px dashed ${T.outlineVariant}`,
                        }}>
                        {doc.bestandsnaam || 'Klantbriefing'}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant }}>
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString('nl-NL') : ''}
                      {doc.aangemaakt_door ? ` · ${doc.aangemaakt_door}` : ''}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {doc.analyse_markdown ? (
                      <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}
                        onClick={() => setGenResult({ titel: `Briefing-check — ${doc.bestandsnaam || 'klantbriefing'}`, markdown: doc.analyse_markdown })}>
                        <Icon name="fact_check" size={15} /> Check bekijken
                      </Box>
                    ) : (
                      <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem', opacity: checkBusy ? 0.6 : 1 }}
                        onClick={() => heranalyseerBriefingDoc(doc.id)}>
                        <Icon name="auto_awesome" size={15} /> Alsnog laten lezen
                      </Box>
                    )}
                    <Tooltip title="Briefing verwijderen bij dit project" slotProps={stitchTooltipSlotProps}>
                      <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, px: 1 }}
                        onClick={() => verwijderBriefingDoc(doc.id)}>
                        <Icon name="delete" size={15} />
                      </Box>
                    </Tooltip>
                  </Box>
                  {velden.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {velden.map(([naam, waarde]) => (
                        <Tooltip key={naam} title={String(waarde)} slotProps={stitchTooltipSlotProps}>
                          <Chip size="small" label={naam.replace(/_/g, ' ')}
                            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: T.tertiaryContainer + '55', color: T.tertiary }} />
                        </Tooltip>
                      ))}
                    </Box>
                  )}

                  {/* De briefing en de notities die je erbij maakt zijn twee dingen —
                      die van de klant, en die van jezelf tijdens het gesprek. */}
                  {inBewerking ? (
                    <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField size="small" label="Onderwerp" value={briefingEdit.naam}
                        onChange={(e) => setBriefingEdit((b) => ({ ...b, naam: e.target.value }))}
                        placeholder="bv. Briefing gesprek 17 aug" fullWidth />
                      <TextField size="small" label="Notities bij de briefing" multiline minRows={3}
                        value={briefingEdit.notities}
                        onChange={(e) => setBriefingEdit((b) => ({ ...b, notities: e.target.value }))}
                        placeholder="Wat er tijdens het gesprek is gezegd en niet in het document staat…" fullWidth />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}
                          onClick={() => bewaarBriefingWijziging(doc.id)}>
                          <Icon name="save" size={15} /> Bewaren
                        </Box>
                        <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}
                          onClick={() => setBriefingEdit(null)}>Annuleren</Box>
                      </Box>
                    </Box>
                  ) : notities ? (
                    <Box sx={{ mt: 1.25, p: 1, borderRadius: '0.5rem', bgcolor: T.surfaceHigh + '55' }}>
                      <Typography variant="caption" sx={{ ...stitchSectionLabel, display: 'block', mb: 0.25 }}>
                        Notities
                      </Typography>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text, whiteSpace: 'pre-wrap' }}>
                        {notities}
                      </Typography>
                      <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.3, px: 1, fontSize: '0.72rem', mt: 0.75 }}
                        onClick={() => setBriefingEdit({ id: doc.id, naam: doc.bestandsnaam || '', notities })}>
                        <Icon name="edit" size={13} /> Bewerken
                      </Box>
                    </Box>
                  ) : (
                    <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.3, px: 1, fontSize: '0.72rem', mt: 1 }}
                      onClick={() => setBriefingEdit({ id: doc.id, naam: doc.bestandsnaam || '', notities: '' })}>
                      <Icon name="add_notes" size={13} /> Notities toevoegen
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Stille secundaire zone (planning-gauntlet, 25 sep): gereedschap,
          documenten en dossier als één regel koppen (kleiner, grijs); wat je
          openklapt verschijnt eronder; de Details-links altijd als laatste. */}
      <Box sx={stilleZoneSx}>
        {/* Gereedschap — standaard ingeklapt tot één regel, open als tekstlinks.
            Altijd renderen: 'E-mail koppelen' hoort in elke fase te kunnen. */}
        <Box className="zone-deel">
          <Box component="button" type="button" className="zone-kop" onClick={() => wisselGereedschap()}
            aria-expanded={gereedschapOpen} sx={{ ...stilleLinkSx, color: T.text, fontWeight: 600 }}>
            <Icon name={gereedschapOpen ? 'expand_more' : 'chevron_right'} size={16} />
            Gereedschap
            <Box component="span" sx={{ color: T.outline, fontWeight: 400 }}>({aantalGereedschap})</Box>
          </Box>
          {gereedschapOpen && (
            <Box sx={{ display: 'flex', columnGap: 2.5, rowGap: 1, flexWrap: 'wrap', pl: 2.5 }}>
              {(meta.dialogActions || []).map((actie) => (
                <Box key={actie.key} component="button" type="button" onClick={() => onOpenDialog?.(actie.key)} sx={stilleLinkSx}>
                  {actie.label}
                </Box>
              ))}
              <Box component="button" type="button" onClick={() => setMailKoppelOpen((v) => !v)}
                sx={{ ...stilleLinkSx, ...(mailKoppelOpen ? { color: T.text, fontWeight: 600 } : {}) }}>
                E-mail koppelen
              </Box>
              {meta.tools.map((tool) => (
                <Tooltip key={tool.path + tool.label} slotProps={stitchTooltipSlotProps}
                  title={tool.supportsContext ? 'Opent met dit project' : 'Opent zonder projectcontext — zoek het project daar zelf op'}>
                  <Box component="button" type="button" onClick={() => navigate(buildToolUrl(tool, project))} sx={stilleLinkSx}>
                    {tool.label}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
          {/* Mail/notulen aan het project hangen — zoeker uit de offerte-generator */}
          {mailKoppelOpen && (
            <Box sx={{ pl: 2.5 }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: TEKST, color: T.outline, mb: 0.5 }}>
                Zoek een e-mail, OneNote-pagina of meeting en koppel hem als projectnotitie (zichtbaar onder Notes).
              </Typography>
              <SourceSearchBox onAddText={koppelBronAlsNotitie} label="Zoek in e-mail, notulen & meetings (klantnaam of onderwerp)" />
            </Box>
          )}
        </Box>

        {/* Documenten van deze fase: het ingevulde document voorop, templates als startpunt */}
        <FaseTemplates fase={phase.phase_key} projectRecId={projectRecId}
          projectNumber={project?.fields?.Projectnummer || ''} />

        {/* Projectdossier — vervangt de Airtable-briefing-doorgifte (die tabel is
            bevroren: 7 records ooit, 71 velden nooit ingevuld). Kern op elke fase;
            op Productie het draaidagblok (de iPad-test), en het live offerte-blok
            bij offerte/opstart. */}
        <DossierBlok
          projectRecId={projectRecId}
          projectNumber={project?.fields?.Projectnummer || project?.projectNumber}
          sectie={phase.phase_key === 'productie' ? 'productie' : 'kern'}
          toonVast={['offertefase', 'opstartfase'].includes(phase.phase_key)}
          standaardIngeklapt={!['opstartfase', 'offertefase', 'concept'].includes(phase.phase_key)}
        />

        {/* Detailpanelen (de oude tabbladen): wat iets bevat staat als stille
            link in beeld; lege/onbekende achter "meer" — nooit onbereikbaar. */}
        {meta.panels.length > 0 && (
          <Box className="zone-details" sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 1, flexWrap: 'wrap' }}>
            <Typography className="zone-titel" sx={stilleKopSx}>Details</Typography>
            {[...details.zichtbaar, ...(toonAlleDetails ? details.overig : [])].map(({ key, aantal }) => (
              <Box key={key} component="button" type="button" onClick={() => onOpenPaneel(key)} sx={stilleLinkSx}>
                {PANEL_LABELS[key] || key}{aantal ? ` (${aantal})` : ''}
              </Box>
            ))}
            {details.overig.length > 0 && (
              <Box component="button" type="button" onClick={() => setToonAlleDetails((v) => !v)}
                sx={{ ...stilleLinkSx, color: T.outline }}>
                {toonAlleDetails ? 'minder' : details.zichtbaar.length
                  ? `meer (${details.overig.length})`
                  : `${details.overig.map((d) => (PANEL_LABELS[d.key] || d.key).toLowerCase()).join(', ')}${
                    details.overig.every((d) => d.aantal === 0) ? ' — nog leeg' : ' — tonen'}`}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ConceptTrainer open={trainerOpen} onClose={() => setTrainerOpen(false)} />

      {/* Mail/tekst plakken voor de briefing-check */}
      <Dialog open={plakOpen} onClose={() => setPlakOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: stitchDialogPaper }}>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>Klantbriefing plakken</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 1 }}>
            Plak de mail of tekst van de klant. Die wordt bewaard bij dit project en daarna
            gelezen: wat staat erin, wat ontbreekt (deliverables, lengtes, ondertiteling,
            kanalen/formaten) en wat vraag je na.
          </Typography>
          <textarea value={plakTekst} onChange={(e) => setPlakTekst(e.target.value)} rows={12}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6,
              border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, resize: 'vertical' }}
            placeholder="Plak hier de mail of briefing-tekst…" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box component="span" onClick={() => setPlakOpen(false)} sx={stitchOutlineBtn}>Annuleren</Box>
          <Box component="span" onClick={() => bewaarBriefingTekst(plakTekst, 'geplakte tekst', 'plak')}
            sx={{ ...stitchGradientBtn, opacity: (!plakTekst.trim() || checkBusy) ? 0.5 : 1 }}>
            {checkBusy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <Icon name="auto_awesome" size={16} />}
            Bewaren bij project
          </Box>
        </DialogActions>
      </Dialog>

      {/* Debrief-resultaat */}
      <GenResultDialog result={genResult} onClose={() => setGenResult(null)} projectRecId={projectRecId} />
    </Box>
  );
}

export default StagePanel;
